import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useApi from './useApi'

const addMinutes = (date, mins) => new Date(date.getTime() + mins * 60000)

// Converts a JS Date to an ISO weekday number: 1=Monday … 7=Sunday
export const toIsoDay = (date) => date.getDay() === 0 ? 7 : date.getDay()

// Returns true if slotTime falls within any of the shiftSlots.
// Checks both boardDate and the following day to handle night shifts that span midnight.
export const isSlotInShift = (slotTime, shiftSlots, boardDate) => {
    if (!shiftSlots || shiftSlots.length === 0) return true
    const nextDay = new Date(boardDate.getTime() + 86400000)
    return shiftSlots.some(s => {
        const parts = String(s.startTime).split(':')
        const h = Number(parts[0])
        const m = parts.length > 1 ? Number(parts[1]) : 0
        for (const anchor of [boardDate, nextDay]) {
            const windowStart = new Date(anchor)
            windowStart.setHours(h, m, 0, 0)
            const windowEnd = new Date(windowStart.getTime() + s.duration * 3600000)
            if (slotTime >= windowStart && slotTime < windowEnd) return true
        }
        return false
    })
}

// Groups consecutive out-of-shift slots into contiguous zones [{start, end}]
export const computeUnavailableZones = (slots, effectiveShiftSlots, boardDate, slotMinutes) => {
    if (!effectiveShiftSlots || slots.length === 0) return []
    const zones = []
    let zoneStart = null
    slots.forEach(slot => {
        const outside = !isSlotInShift(slot, effectiveShiftSlots, boardDate)
        if (outside && zoneStart === null) {
            zoneStart = slot
        } else if (!outside && zoneStart !== null) {
            zones.push({ start: zoneStart, end: slot })
            zoneStart = null
        }
    })
    if (zoneStart !== null) {
        zones.push({ start: zoneStart, end: addMinutes(slots[slots.length - 1], slotMinutes) })
    }
    return zones
}

const snapToSlot = (date, slotMinutes) => {
    const slotMs = slotMinutes * 60000
    return new Date(Math.ceil(date.getTime() / slotMs) * slotMs)
}

const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && aEnd > bStart

export const findFirstAvailableSlot = (fromTime, totalTimeMinutes, bookings, earliestStart, slotMinutes, excludeOpId = null, shiftSlots = null, boardDate = null, startField = 'plannedStart', endField = 'plannedEnd') => {
    const slotMs = slotMinutes * 60000
    const floor = Math.max(
        fromTime.getTime(),
        earliestStart ? snapToSlot(earliestStart, slotMinutes).getTime() : fromTime.getTime()
    )
    let candidate = new Date(Math.ceil(floor / slotMs) * slotMs)

    for (let i = 0; i < 200; i++) {
        const candidateEnd = addMinutes(candidate, totalTimeMinutes)
        const outsideShift = shiftSlots && boardDate && !isSlotInShift(candidate, shiftSlots, boardDate)
        const blocked = !outsideShift && bookings.some(b => {
            if (!b[startField] || !b[endField]) return false
            if (excludeOpId && b.id === excludeOpId) return false
            return overlaps(candidate, candidateEnd, new Date(b[startField]), new Date(b[endField]))
        })
        if (!outsideShift && !blocked) return candidate
        candidate = addMinutes(candidate, slotMinutes)
    }
    return null
}

export default function useWorkPlanner({ jobId, selectedOpId, shiftStart, shiftHours, bufferHours, slotMinutes, plantId, plannerConfig }) {
    const { getMe, putMe } = useApi()
    const queryClient = useQueryClient()

    const { entities, fields, shiftChain, invalidateEntities } = plannerConfig

    const OPS_KEY       = ['workPlanner', entities.task, 'ops', jobId]
    const RES_KEY_BASE  = `workPlanner-${entities.resource}`
    const BOOK_KEY_BASE = `workPlanner-${entities.task}-book`

    // ── Tasks for selected job ────────────────────────────────────────────────
    const { data: opsResult, isLoading: opsLoading } = useQuery({
        queryKey: OPS_KEY,
        queryFn:  () => getMe(`${entities.job}/${jobId}/${entities.task}?orderBy=${fields.sequence}&direction=asc`),
        enabled:  !!jobId,
        staleTime: 0
    })

    const [ops, setOps] = useState([])

    useEffect(() => { setOps([]) }, [jobId])
    useEffect(() => { if (Array.isArray(opsResult?.data)) setOps(opsResult.data) }, [opsResult])

    const pendingOps   = useMemo(() => ops.filter(o => !o[fields.startTime] || !o[fields.resourceId]), [ops])
    const scheduledOps = useMemo(() => ops.filter(o =>  o[fields.startTime] &&  o[fields.resourceId]), [ops])
    const selectedOp   = useMemo(() => ops.find(o => o.id === selectedOpId) ?? null, [ops, selectedOpId])
    const currentOpIds = useMemo(() => new Set(ops.map(o => o.id)), [ops])

    // ── Earliest start constraint (max endTime of prior tasks in sequence) ────
    const earliestStart = useMemo(() => {
        if (!selectedOp) return null
        const priorEnds = scheduledOps
            .filter(o => o[fields.sequence] < selectedOp[fields.sequence] && o[fields.endTime])
            .map(o => new Date(o[fields.endTime]).getTime())
        if (priorEnds.length === 0) return null
        return new Date(Math.max(...priorEnds))
    }, [selectedOp, scheduledOps])

    // ── All capability IDs from all tasks, ordered by first occurrence ────────
    const allCapabilityIds = useMemo(() => {
        const seen = new Set()
        const result = []
        ;[...ops].sort((a, b) => a[fields.sequence] - b[fields.sequence]).forEach(op => {
            if (op[fields.capabilityId] && !seen.has(op[fields.capabilityId])) {
                seen.add(op[fields.capabilityId])
                result.push(op[fields.capabilityId])
            }
        })
        return result
    }, [ops])

    const allCapsKey = allCapabilityIds.join(',')

    const { data: allResResult, isLoading: resourcesLoading } = useQuery({
        queryKey: [RES_KEY_BASE, 'all', allCapsKey, plantId ?? ''],
        queryFn: async () => {
            const plantFilter = plantId ? `;plantId:${plantId}` : ''
            if (allCapabilityIds.length === 0) {
                // No capability requirements — fetch all resources of this type
                const q = plantId ? `?filter=plantId:${plantId}` : ''
                const result = await getMe(`${entities.resource}${q}`)
                return { __all__: result?.data ?? [] }
            }
            const results = await Promise.all(
                allCapabilityIds.map(capId => getMe(`${entities.resource}?filter=${fields.capabilityId}:${capId}${plantFilter}`))
            )
            const map = {}
            allCapabilityIds.forEach((capId, i) => { map[capId] = results[i]?.data ?? [] })
            return map
        },
        enabled: !!jobId,
        staleTime: 60_000
    })

    // Stable ordered list — all resources in capability-sequence order, deduplicated
    const allResources = useMemo(() => {
        if (!allResResult) return []
        const seen = new Set()
        const result = []
        if (allCapabilityIds.length === 0) {
            // No capability filter — use the flat list fetched above
            ;(allResResult.__all__ ?? []).forEach(r => {
                if (!seen.has(r.id)) { seen.add(r.id); result.push(r) }
            })
        } else {
            allCapabilityIds.forEach(capId => {
                ;(allResResult[capId] ?? []).forEach(r => {
                    if (!seen.has(r.id)) { seen.add(r.id); result.push(r) }
                })
            })
        }
        return result
    }, [allResResult, allCapabilityIds])

    // ── Shift resolution via configurable shiftChain ──────────────────────────
    const { data: resourceEffectiveShifts = {} } = useQuery({
        queryKey: ['shiftResolution', entities.resource, allResources.map(r => r.id).join(',')],
        queryFn: async () => {
            const directStep     = shiftChain.find(s =>  s.direct)
            const nonGlobalSteps = shiftChain.filter(s => !s.global && !s.direct)
            const globalStep     = shiftChain.find(s =>  s.global)

            // Collect unique parent IDs per entity
            const idsByEntity = {}
            nonGlobalSteps.forEach(step => {
                idsByEntity[step.entity] = [...new Set(allResources.map(r => r[step.refField]).filter(Boolean))]
            })

            // For direct steps: shiftId lives directly on the resource (e.g. serviceTechnician.shiftId)
            const directShiftIds = directStep
                ? [...new Set(allResources.map(r => r.shiftId).filter(Boolean))]
                : []

            // Fetch all parents + global settings + direct shifts in parallel
            const [parentResults, globalRes, directShiftResults] = await Promise.all([
                Promise.all(nonGlobalSteps.map(step =>
                    Promise.all((idsByEntity[step.entity] ?? []).map(id => getMe(`${step.entity}/${id}`)))
                )),
                globalStep ? getMe(globalStep.global) : Promise.resolve(null),
                Promise.all(directShiftIds.map(id => getMe(`shift/${id}`)))
            ])

            // Build lookup maps: entity -> { id -> data }
            const parentMap = {}
            nonGlobalSteps.forEach((step, si) => {
                parentMap[step.entity] = {}
                ;(idsByEntity[step.entity] ?? []).forEach((id, ii) => {
                    const d = parentResults[si][ii]?.data
                    if (d) parentMap[step.entity][id] = d
                })
            })

            const globalData = globalRes?.data ?? globalRes

            // Build direct shift map { shiftId -> shiftData }
            const directShiftMap = {}
            directShiftIds.forEach((id, i) => {
                const d = directShiftResults[i]?.data ?? directShiftResults[i]
                if (d?.shiftSlots) directShiftMap[id] = d
            })

            // Collect all referenced shift IDs (from non-direct, non-global steps + global)
            const shiftIds = [...new Set([
                ...nonGlobalSteps.flatMap(step =>
                    Object.values(parentMap[step.entity] ?? {}).map(p => p[step.shiftField])
                ),
                globalStep ? globalData?.[globalStep.shiftField] : null
            ].filter(Boolean))]

            const shiftResults = await Promise.all(shiftIds.map(id => getMe(`shift/${id}`)))
            const shiftMap = {}
            shiftResults.forEach((r, i) => {
                const d = r?.data ?? r
                if (d?.shiftSlots) shiftMap[shiftIds[i]] = d
            })

            // Resolve per resource: walk chain in order, first match wins.
            // Resolved value: { shiftSlots, activeDays } — activeDays is 1–7 ISO weekday array;
            // empty/absent means all days are active (backward-compatible default).
            const resolved = {}
            allResources.forEach(resource => {
                for (const step of shiftChain) {
                    if (step.direct) {
                        // shiftId is on the resource itself (e.g. serviceTechnician)
                        if (resource.shiftId && directShiftMap[resource.shiftId]?.shiftSlots) {
                            resolved[resource.id] = {
                                shiftSlots: directShiftMap[resource.shiftId].shiftSlots,
                                activeDays: directShiftMap[resource.shiftId].activeDays ?? []
                            }
                            break
                        }
                    } else if (step.global) {
                        const shiftId = globalData?.[step.shiftField]
                        if (shiftId && shiftMap[shiftId]?.shiftSlots) {
                            resolved[resource.id] = {
                                shiftSlots: shiftMap[shiftId].shiftSlots,
                                activeDays: shiftMap[shiftId].activeDays ?? []
                            }
                            break
                        }
                    } else {
                        const parent = parentMap[step.entity]?.[resource[step.refField]]
                        if (parent?.[step.shiftField] && shiftMap[parent[step.shiftField]]?.shiftSlots) {
                            resolved[resource.id] = {
                                shiftSlots: shiftMap[parent[step.shiftField]].shiftSlots,
                                activeDays: shiftMap[parent[step.shiftField]].activeDays ?? []
                            }
                            break
                        }
                    }
                }
            })
            return resolved
        },
        enabled: allResources.length > 0,
        staleTime: 60_000
    })

    // Resource IDs eligible for the selected task (can accept drops)
    const eligibleResourceIds = useMemo(() => {
        if (!selectedOp) return new Set()
        // No capability required → all loaded resources are eligible
        if (!selectedOp[fields.capabilityId]) return new Set(allResources.map(r => r.id))
        if (!allResResult) return new Set()
        return new Set((allResResult[selectedOp[fields.capabilityId]] ?? []).map(r => r.id))
    }, [selectedOp, allResResult, allResources, fields])

    // ── Bookings for all visible resources (in time window) ───────────────────
    const displayEnd = useMemo(
        () => addMinutes(shiftStart, (shiftHours + (bufferHours ?? 0)) * 60),
        [shiftStart, shiftHours, bufferHours]
    )

    const allVisibleResIds = useMemo(() => allResources.map(r => r.id), [allResources])

    const { data: bookingsResult, isLoading: bookingsLoading } = useQuery({
        queryKey: [BOOK_KEY_BASE, allVisibleResIds.join(','), shiftStart.getTime(), displayEnd.getTime()],
        queryFn: async () => {
            if (allVisibleResIds.length === 0) return {}
            const results = await Promise.all(
                allVisibleResIds.map(resId => getMe(`${entities.task}?filter=${fields.resourceId}:${resId}`))
            )
            const map = {}
            allVisibleResIds.forEach((resId, i) => {
                const all = results[i]?.data ?? []
                map[resId] = all.filter(b => {
                    if (!b[fields.startTime] || !b[fields.endTime]) return false
                    return overlaps(new Date(b[fields.startTime]), new Date(b[fields.endTime]), shiftStart, displayEnd)
                })
            })
            return map
        },
        enabled:  allVisibleResIds.length > 0,
        staleTime: 0
    })

    const bookings = bookingsResult ?? {}

    // ── Status collection — fetched once, used to resolve statusName ─────────
    const { data: statusCollectionResult } = useQuery({
        queryKey: ['statusCollection', fields.statusCollection],
        queryFn:  () => getMe(fields.statusCollection),
        enabled:  !!fields.statusCollection,
        staleTime: Infinity
    })

    const statusNameMap = useMemo(() => {
        if (!fields.statusCollection || !statusCollectionResult?.data) return {}
        const items = Array.isArray(statusCollectionResult.data) ? statusCollectionResult.data : []
        return items.reduce((acc, s) => {
            if (s.id) acc[s.id] = s.name
            return acc
        }, {})
    }, [statusCollectionResult, fields.statusCollection])

    // ── Invalidate planner-internal + external entity caches ─────────────────
    const invalidateAfterSave = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: OPS_KEY })
        queryClient.invalidateQueries({ queryKey: [BOOK_KEY_BASE] })
        const entitiesToInvalidate = invalidateEntities ?? [entities.task]
        entitiesToInvalidate.forEach(entity => {
            queryClient.invalidateQueries({ queryKey: ['list', entity] })
            queryClient.invalidateQueries({ queryKey: [entity] })
        })
    }, [queryClient, invalidateEntities, entities.task, jobId])

    // ── Schedule / unschedule a task ──────────────────────────────────────────
    const unscheduleOp = useCallback(async (op) => {
        const unscheduledStatusName = fields.statusNameField ? (statusNameMap['draft'] ?? null) : null
        const statusPatch = fields.statusField ? {
            [fields.statusField]: 'draft',
            ...(fields.statusNameField && unscheduledStatusName !== null && { [fields.statusNameField]: unscheduledStatusName })
        } : {}
        const resourceCodePatch = fields.resourceCode ? { [fields.resourceCode]: null } : {}
        const cleared = { ...op,
            [fields.resourceId]: null, [fields.resourceName]: null,
            [fields.startTime]:  null, [fields.endTime]:      null,
            ...resourceCodePatch, ...statusPatch
        }
        setOps(prev => prev.map(o => o.id === op.id ? cleared : o))
        try {
            const result = await putMe(entities.task, {
                [fields.resourceId]: null, [fields.resourceName]: null,
                [fields.startTime]:  null, [fields.endTime]:      null,
                ...resourceCodePatch, ...statusPatch
            }, op.id)
            if (result?.error) throw new Error('server_error')
            invalidateAfterSave()
        } catch {
            setOps(prev => prev.map(o => o.id === op.id ? op : o))
            throw new Error('unschedule_failed')
        }
    }, [putMe, invalidateAfterSave, statusNameMap, jobId])

    const scheduleOp = useCallback(async (op, resource, slotTime) => {
        const startTime    = slotTime.toISOString()
        const endTime      = addMinutes(slotTime, op[fields.totalTime] ?? 0).toISOString()
        const resourceName = resource[fields.resourceLabel] ?? resource.name
        const scheduledStatusName = fields.statusNameField ? (statusNameMap['scheduled'] ?? null) : null
        const statusPatch  = fields.statusField ? {
            [fields.statusField]: 'scheduled',
            ...(fields.statusNameField && scheduledStatusName !== null && { [fields.statusNameField]: scheduledStatusName })
        } : {}
        const resourceCodePatch = fields.resourceCode ? { [fields.resourceCode]: resource[fields.resourceSubLabel] ?? null } : {}

        const updated = { ...op,
            [fields.resourceId]: resource.id, [fields.resourceName]: resourceName,
            [fields.startTime]:  startTime,   [fields.endTime]:      endTime,
            ...resourceCodePatch, ...statusPatch
        }
        setOps(prev => prev.map(o => o.id === op.id ? updated : o))

        try {
            const result = await putMe(entities.task, {
                [fields.resourceId]: resource.id, [fields.resourceName]: resourceName,
                [fields.startTime]:  startTime,   [fields.endTime]:      endTime,
                ...resourceCodePatch, ...statusPatch
            }, op.id)
            if (result?.error) throw new Error('server_error')
            invalidateAfterSave()
        } catch {
            setOps(prev => prev.map(o => o.id === op.id ? op : o))
            throw new Error('scheduling_failed')
        }
    }, [putMe, invalidateAfterSave, statusNameMap, jobId])

    const invalidateOps = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: OPS_KEY })
    }, [queryClient, jobId])

    return {
        ops, pendingOps, scheduledOps, selectedOp,
        currentOpIds,
        opsLoading,
        allResources, eligibleResourceIds,
        resourcesByCapability: allResResult ?? {},
        resourcesLoading,
        bookings, bookingsLoading,
        earliestStart,
        resourceEffectiveShifts,
        scheduleOp, unscheduleOp, invalidateOps
    }
}
